import React, { useEffect, useRef, useState } from 'react';
import { WithWistiaContext } from 'utils/WistiaContext';
import pollForDefinition from 'utils/pollForDefinition';
import pollForWistiaPlayer from 'utils/pollForWistiaPlayer';
import InlinePlayerContainer from './styles';

const InlinePlayer = ({
  active,
  autoPlay,
  isPlaying,
  setIsPlaying,
  videoId,
  wrapperClass,
  overlay,
  ...otherProps
}) => {
  const [isConsentClosed, setIsConsentClosed] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const videoRef = useRef(null);
  const wistiaPlayerRef = useRef(null);
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    const isDirectAccess = !document?.referrer;

    if (!isDirectAccess) {
      setIsConsentClosed(true);
    } else if (window?.OneTrust) {
      pollForDefinition('OneTrust', 'OnConsentChanged', () =>
        OneTrust.OnConsentChanged(() => setIsConcentClosed(true)),
      );
    }
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      pollForWistiaPlayer(videoRef, (playerInstance) => {
        wistiaPlayerRef.current = playerInstance;

        playerInstance.bind('play', () => {
          setHasPlayed(true);
          if (setIsPlaying) setIsPlaying(true);
        });
        playerInstance.bind('pause', () => {
          if (setIsPlaying) setIsPlaying(false);
        });
        playerInstance.bind('end', () => {
          if (setIsPlaying) setIsPlaying(false);
        });

        playerInstance.bind('enterfullscreen', () => {
          setIsFullScreen(true);
        });
        playerInstance.bind('cancelfullscreen', () => {
          setIsFullScreen(false);
        });

        if (active) {
          if (autoPlay) {
            playerInstance.play();
          } else {
            playerInstance.pause();
          }
        }

        if (isConsentClosed) {
          playerInstance.unmute();
        }
      });
    }

    return () => {
      if (wistiaPlayerRef.current) {
        wistiaPlayerRef.current.unbind('play');
        wistiaPlayerRef.current.unbind('pause');
        wistiaPlayerRef.current.unbind('end');
        wistiaPlayerRef.current.unbind('enterfullscreen');
        wistiaPlayerRef.current.unbind('cancelfullscreen');
      }
      wistiaPlayerRef.current = null;
    };
  }, [active, autoPlay, isConsentClosed, videoId, setIsPlaying]);

  useEffect(() => {
    if (isConsentClosed && wistiaPlayerRef.current) {
      wistiaPlayerRef.current.unmute();
    }
  }, [isConsentClosed]);

  useEffect(() => {
    const player = wistiaPlayerRef.current;

    if (player && active) {
      if (isPlaying && player.state() !== 'playing') {
        player.play();
      } else if (!isPlaying && player.state() === 'playing') {
        player.pause();
      }
    }
  }, [isPlaying, active]);

  return (
    <InlinePlayerContainer
      isFullScreen={isFullScreen}
      isPlaying={isPlaying}
      className={wrapperClass}
      {...otherProps}
    >
      {!hasPlayed && overlay}
      <div
        className={`wistia_embed wistia_async_${videoId} wistia-video playsinline videoFoam=true controlsVisibleOnLoad=false`}
        ref={videoRef}
      />
      <div style={{ display: 'none' }} />
    </InlinePlayerContainer>
  );
};

export default WithWistiaContext(InlinePlayer);
